// 🎤 CastingPage - Page de candidature au casting Rock'In Agde
// Présente les modalités et intègre le formulaire Tally de candidature
// Utilisé dans : src/App.jsx (route "/casting")

import { Mic2, Users, Music, Calendar, MapPin, CheckCircle } from 'lucide-react'
import castingData from '@/locales/casting.json'
import homeData from '@/locales/home.json'
import HeroSection from '@/components/home/HeroSection'

/**
 * Page de candidature au casting du festival
 * Structure : Hero + Infos pratiques + Formulaire Tally embarqué + CTA final
 * Formulaire tiers intégré via iframe pour simplifier la gestion des candidatures
 */
export default function CastingPage() {
  const { hero, howToParticipate, infoCards, form, cta, details } = castingData
  const { stats } = homeData

  return (
    <div className="md:pt-16">
      {/* Hero Section */}
      <HeroSection
        badge={{ icon: Mic2, text: hero.badge }}
        title={hero.title}
        description={hero.description}
        features={hero.features.map(feature => ({ text: feature }))}
      />

      {/* Infos pratiques */}
      <section className="section-padding bg-white dark:bg-neutral-900">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-display text-primary-600 dark:text-primary-400 text-center mb-12">
              {howToParticipate.title}
            </h2>

            <div className="space-y-4 text-lg text-neutral-700 dark:text-neutral-300 leading-relaxed mb-12">
              {howToParticipate.description.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>

            {/* Infos cards */}
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <div className="bg-gradient-to-br from-primary-50 to-white dark:from-neutral-800 dark:to-neutral-900 p-6 rounded-2xl border border-primary-100 dark:border-neutral-700">
                <Calendar className="text-primary-500 mb-4" size={32} />
                <h3 className="font-bold text-lg text-neutral-900 dark:text-neutral-100 mb-2">
                  {infoCards.deadline.title}
                </h3>
                <p className="text-neutral-700 dark:text-neutral-300">
                  {details.deadline}
                </p>
              </div>

              <div className="bg-gradient-to-br from-primary-50 to-white dark:from-neutral-800 dark:to-neutral-900 p-6 rounded-2xl border border-primary-100 dark:border-neutral-700">
                <MapPin className="text-primary-500 mb-4" size={32} />
                <h3 className="font-bold text-lg text-neutral-900 dark:text-neutral-100 mb-2">
                  {infoCards.location.title}
                </h3>
                <p className="text-neutral-700 dark:text-neutral-300">
                  {details.location}
                </p>
              </div>

              <div className="bg-gradient-to-br from-primary-50 to-white dark:from-neutral-800 dark:to-neutral-900 p-6 rounded-2xl border border-primary-100 dark:border-neutral-700">
                <Users className="text-primary-500 mb-4" size={32} />
                <h3 className="font-bold text-lg text-neutral-900 dark:text-neutral-100 mb-2">
                  {infoCards.audience.title}
                </h3>
                <p className="text-neutral-700 dark:text-neutral-300">
                  {stats.spectators}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Formulaire Tally */}
      <section className="section-padding bg-neutral-50 dark:bg-neutral-800">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-display text-primary-600 dark:text-primary-400 mb-4">
                {form.title}
              </h2>
              <p className="text-lg text-neutral-700 dark:text-neutral-300">
                {form.subtitle}
              </p>
            </div>

            {/* Iframe Tally */}
            <div className="bg-white dark:bg-neutral-900 rounded-2xl shadow-2xl overflow-hidden">
              <iframe
                src={form.url}
                width="100%"
                height="800"
                frameBorder="0"
                marginHeight="0"
                marginWidth="0"
                title={form.iframeTitle}
                className="w-full"
              />
            </div>

            {/* Liens alternatifs */}
            <div className="mt-8 text-center">
              <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">
                {form.alternativesText}
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                {form.alternatives.map((url, index) => (
                  <a
                    key={index}
                    href={url.startsWith('http') ? url : `https://${url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    <Music size={16} />
                    {form.alternativeLinkText}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="section-padding bg-gradient-to-r from-primary-500 to-primary-600 text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-5xl font-display font-bold mb-6">
            {cta.title}
          </h2>
          <p className="text-xl text-neutral-100 mb-8 max-w-2xl mx-auto">
            {cta.description}
          </p>
          <a
            href={form.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white text-primary-600 hover:bg-neutral-100 px-8 py-4 rounded-lg font-bold text-lg transition-all shadow-xl hover:scale-105"
          >
            <Mic2 size={24} />
            {cta.buttonText}
          </a>
        </div>
      </section>
    </div>
  )
}
